/* ═══════════════════════════════════════════════════════════════
   The Designer — spec-domain commands and document invariants.

   ProseMirror already owns the caret, selection, undo history, IME and
   the clipboard; nothing here re-implements any of that. These are the
   SPEC rules on top:

     · Tab / Shift+Tab promote and demote a paragraph's level
     · every block carries exactly one stable, unique id
     · Ctrl+S flushes the autosave instead of printing the page
     · Ctrl+D duplicates the selected blocks; Ctrl+F opens find
     · whole-block operations: duplicate, delete, insert image /
       table / smart token — the menu and toolbar call these
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { Transaction } from '@tiptap/pm/state';
import type { EditorState } from '@tiptap/pm/state';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import { newBlockId } from './schema';
import { buildTableNode, TABLE_NODE_NAME } from './tables';
import { openFind } from './search';

export const uniqueIdsPluginKey = new PluginKey('designerUniqueIds');

/** Names of every node type that represents one ContentBlock. */
export const BLOCK_NODE_NAMES = ['specParagraph', TABLE_NODE_NAME, 'specImage', 'smartBlock'];

/**
 * Every block owns exactly one id, forever.
 *
 * Splitting a paragraph, pasting, and duplicating all produce nodes that
 * share their source's `blockId`. Rather than patch each of those paths, this
 * plugin restores the invariant after ANY transaction — so a path we haven't
 * thought of yet cannot corrupt the document either. Ids are only ever minted,
 * never reassigned on a block that already holds a unique one, which keeps
 * issuance diffs stable across edits.
 */
export const UniqueBlockIds = Extension.create({
  name: 'designerUniqueIds',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: uniqueIdsPluginKey,
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          const seen = new Set<string>();
          let tr: Transaction | null = null;
          newState.doc.forEach((node, offset) => {
            if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
            const id = node.attrs.blockId as string;
            if (id && !seen.has(id)) {
              seen.add(id);
              return;
            }
            const fresh = newBlockId();
            seen.add(fresh);
            tr = (tr || newState.tr).setNodeAttribute(offset, 'blockId', fresh);
          });
          // Housekeeping must never enter the undo stack. If it did, every
          // Ctrl+Z would first undo an id fix and only then the user's edit,
          // and redo would replay the two out of step — the document comes
          // back rearranged. (Caught by the R1 harness's undo/redo burst.)
          if (tr) (tr as Transaction).setMeta('addToHistory', false);
          return tr;
        },
      }),
    ];
  },
});

export interface SpecKeymapOptions {
  /** Ctrl/Cmd+S — flush pending autosave. */
  onSave?: () => void;
  /** Escape — the keyboard user's way out of the document (see below). */
  onEscape?: () => void;
}

/** Clamp to the nine MasterFormat levels. */
const clampLevel = (n: number) => Math.max(1, Math.min(9, n));

/**
 * Change the level of every block touched by the selection. Word's Tab
 * behaviour, applied to a spec: the paragraph moves between MasterFormat
 * levels, its number and indent follow, and the text is never touched.
 */
function shiftLevel(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  delta: number
) {
  const { from, to } = state.selection;
  let tr: Transaction | null = null;
  let changed = false;
  state.doc.forEach((node, offset) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    const start = offset;
    const end = offset + node.nodeSize;
    if (end <= from || start >= to) {
      // Not in range — but a collapsed caret sits exactly at a boundary.
      if (!(from === to && from >= start && from <= end)) return;
    }
    const next = clampLevel(((node.attrs.level as number) || 1) + delta);
    if (next === node.attrs.level) return;
    changed = true;
    tr = (tr || state.tr).setNodeAttribute(start, 'level', next);
  });
  if (!changed || !tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/**
 * The spec keymap. Everything NOT listed here is deliberately left to
 * ProseMirror: Enter splits, Backspace merges, Ctrl+Z/Y undo, arrows and
 * selection all behave like a document editor because the framework — not
 * this app — implements them.
 */
export const SpecKeymap = Extension.create<SpecKeymapOptions>({
  name: 'designerKeymap',

  addOptions() {
    return { onSave: undefined, onEscape: undefined };
  },

  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => shiftLevel(editor.state, editor.view.dispatch.bind(editor.view), +1),
      'Shift-Tab': ({ editor }) =>
        shiftLevel(editor.state, editor.view.dispatch.bind(editor.view), -1),
      'Mod-s': () => {
        this.options.onSave?.();
        return true;
      },
      // Find — the universal shortcut (DESIGN §1). A collapsed selection
      // opens the bar with the last query; a one-line selection pre-fills it.
      'Mod-f': ({ editor }) => {
        const { state } = editor;
        const { from, to, empty } = state.selection;
        const picked = empty ? '' : state.doc.textBetween(from, to, '\n');
        const seed = picked && !picked.includes('\n') ? picked.slice(0, 500) : undefined;
        return openFind(state, editor.view.dispatch.bind(editor.view), seed);
      },
      // Duplicate the selected blocks. Swallowed even when nothing is
      // selected so the browser's bookmark dialog can never appear.
      'Mod-d': ({ editor }) => {
        if (editor.isEditable) {
          duplicateSelectedBlocks(editor.state, editor.view.dispatch.bind(editor.view));
        }
        return true;
      },
      // Tab belongs to the spec levels, so it cannot also be "move to the
      // next control" — which would leave a keyboard user trapped in the
      // document (WCAG 2.1.2). Escape is the documented way out: it blurs
      // the editor, and the next Tab continues through the page normally.
      Escape: ({ editor }) => {
        (editor.view.dom as HTMLElement).blur();
        this.options.onEscape?.();
        return true;
      },
      // Inline emphasis — the only four character styles that exist. The
      // forma owns everything else, so there is no font/size/colour path.
      // Highlight is the marker pen (Word's Ctrl+Alt+H); Mod+Shift+H is the
      // second binding because Alt chords are unreliable across platforms.
      'Mod-b': ({ editor }) => editor.commands.toggleMark('bold'),
      'Mod-i': ({ editor }) => editor.commands.toggleMark('italic'),
      'Mod-u': ({ editor }) => editor.commands.toggleMark('underline'),
      'Mod-Alt-h': ({ editor }) => editor.commands.toggleMark('highlight'),
      'Mod-Shift-h': ({ editor }) => editor.commands.toggleMark('highlight'),
    };
  },
});

/** Promote/demote from a button or menu, not just the keyboard. */
export function changeLevel(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  delta: number
): boolean {
  return shiftLevel(state, dispatch, delta);
}

/** Set an absolute level on the blocks the selection touches. */
export function setLevel(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  level: number
): boolean {
  const target = clampLevel(level);
  const { from, to } = state.selection;
  let tr: Transaction | null = null;
  state.doc.forEach((node, offset) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    const end = offset + node.nodeSize;
    if (end <= from || offset >= to) {
      if (!(from === to && from >= offset && from <= end)) return;
    }
    if (node.attrs.level === target) return;
    tr = (tr || state.tr).setNodeAttribute(offset, 'level', target);
  });
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/** The top-level blocks the selection touches (a collapsed caret counts the
 *  block it sits in) — the shared footing for every whole-block operation. */
function selectedBlocks(state: EditorState): Array<{ node: PMNode; pos: number }> {
  const { from, to } = state.selection;
  const out: Array<{ node: PMNode; pos: number }> = [];
  state.doc.forEach((node, offset) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    const end = offset + node.nodeSize;
    if (end <= from || offset >= to) {
      if (!(from === to && from >= offset && from <= end)) return;
    }
    out.push({ node, pos: offset });
  });
  return out;
}

/**
 * Duplicate every block the selection touches, inserting the copies right
 * after the last one — Word's Ctrl+D-on-a-row feel. Copies mint fresh ids
 * immediately so issuance diffs never see two blocks claiming one identity.
 */
export function duplicateSelectedBlocks(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  const sel = selectedBlocks(state);
  if (!sel.length) return false;
  const last = sel[sel.length - 1];
  const copies = sel.map(({ node }) =>
    node.type.create({ ...node.attrs, blockId: newBlockId() }, node.content, node.marks)
  );
  if (dispatch) {
    dispatch(state.tr.insert(last.pos + last.node.nodeSize, copies).scrollIntoView());
  }
  return true;
}

/**
 * Delete every block the selection touches — whole blocks, one transaction,
 * recoverable with one Ctrl+Z. Deleting the final block leaves one empty
 * paragraph, because a spec document always has somewhere to put the caret.
 */
export function deleteSelectedBlocks(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  const sel = selectedBlocks(state);
  if (!sel.length) return false;
  const first = sel[0];
  const last = sel[sel.length - 1];
  const end = last.pos + last.node.nodeSize;
  const tr = state.tr;
  if (first.pos === 0 && end === state.doc.content.size) {
    tr.replaceWith(0, state.doc.content.size, emptyParagraph(state.schema));
  } else {
    tr.delete(first.pos, end);
  }
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

function emptyParagraph(schema: Schema): PMNode {
  return schema.nodes.specParagraph.create({
    blockId: newBlockId(),
    level: 1,
    continuation: false,
    excluded: false,
    meta: {},
  });
}

/** Insert a block after the selection's last block, at that block's level.
 *  Atoms (image / smart token) come back selected so the user sees exactly
 *  what landed and where; the table (real editable content, not an atom)
 *  instead puts the caret in its first cell — ready to type. */
function insertBlockAfterSelection(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  build: (schema: Schema, level: number) => PMNode
): boolean {
  const sel = selectedBlocks(state);
  const last = sel.length ? sel[sel.length - 1] : null;
  const insertAt = last ? last.pos + last.node.nodeSize : state.doc.content.size;
  const level = last ? (last.node.attrs.level as number) || 1 : 1;
  const node = build(state.schema, level);
  let tr = state.tr.insert(insertAt, node);
  if (node.type.isAtom) {
    tr = tr.setSelection(NodeSelection.create(tr.doc, insertAt));
  } else if (node.type.name === TABLE_NODE_NAME) {
    tr = tr.setSelection(TextSelection.near(tr.doc.resolve(insertAt + 1), 1));
  }
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/** A figure block from a picked file, at the width it will print. */
export function insertImageBlock(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  dataUrl: string,
  name: string
): boolean {
  return insertBlockAfterSelection(state, dispatch, (schema, level) =>
    schema.nodes.specImage.create({
      blockId: newBlockId(),
      level,
      continuation: false,
      excluded: false,
      meta: {},
      text: name || '',
      imageData: dataUrl,
      imageWidth: 100,
    })
  );
}

/** A fresh 3×3 table with a header row (tables.ts's `buildTableNode` — built
 *  entirely from the framework's own `createTable` utility), inserted after
 *  the current block like every other insert here. */
export function insertTableBlock(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  return insertBlockAfterSelection(state, dispatch, (schema, level) =>
    buildTableNode(schema, level)
  );
}

export interface SmartTokenInsert {
  smartBlockId: string;
  smartBlockKind: string;
  smartParams: Record<string, unknown>;
  /** Resolved content shown on the paper; the export re-resolves live. */
  previewText: string;
}

/** A smart-token block. The declaration (id / kind / params) rides in the
 *  node's meta exactly as it is stored on the ContentBlock; the node stays
 *  the read-only atom the R1 node view renders (authoring is cut — §3.4). */
export function insertSmartTokenBlock(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  token: SmartTokenInsert
): boolean {
  return insertBlockAfterSelection(state, dispatch, (schema, level) =>
    schema.nodes.smartBlock.create({
      blockId: newBlockId(),
      level,
      continuation: false,
      excluded: false,
      meta: {
        smartBlockId: token.smartBlockId,
        smartBlockKind: token.smartBlockKind,
        smartParams: token.smartParams,
      },
      text: token.previewText,
    })
  );
}

/**
 * Pick an image. The desktop build answers with the native dialog, the web
 * build's shim answers with the browser picker — one call, both platforms.
 * Returns a data: URL ready for the specImage node, or null on cancel.
 */
export async function pickImageFile(): Promise<{ dataUrl: string; name: string } | null> {
  if (!window.api?.openImageDialog) return null;
  const result = await window.api.openImageDialog();
  if (!result) return null;
  const dataUrl = result.data.startsWith('data:')
    ? result.data
    : `data:${result.mimeType || 'image/png'};base64,${result.data}`;
  return { dataUrl, name: result.name || '' };
}

/** Toggle "skip at export" on the blocks the selection touches. */
export function toggleExcluded(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined
): boolean {
  const { from, to } = state.selection;
  const targets: Array<{ pos: number; excluded: boolean }> = [];
  state.doc.forEach((node, offset) => {
    if (!BLOCK_NODE_NAMES.includes(node.type.name)) return;
    const end = offset + node.nodeSize;
    if (end <= from || offset >= to) {
      if (!(from === to && from >= offset && from <= end)) return;
    }
    targets.push({ pos: offset, excluded: !!node.attrs.excluded });
  });
  if (!targets.length) return false;
  // One toggle for the whole selection: if any block is included, exclude all.
  const next = targets.some((t) => !t.excluded);
  let tr: Transaction | null = null;
  for (const t of targets) {
    if (t.excluded === next) continue;
    tr = (tr || state.tr).setNodeAttribute(t.pos, 'excluded', next);
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}
